/**
 * shared/format.ts · 格式化 helpers
 */

import { Currency, CurrencySymbol, type CurrencyValue } from '@/constants';
import { i18nStore } from '@/stores/i18nStore';

/** 货币 (默认 VND · 自动 K/M/B 缩写) */
export function fmt(amount: number, currency: CurrencyValue = Currency.VND): string {
  const v = amount || 0;
  const symbol = CurrencySymbol[currency];
  if (currency === Currency.VND) {
    if (Math.abs(v) >= 1e9) return symbol + (v / 1e9).toFixed(1) + 'B';
    if (Math.abs(v) >= 1e6) return symbol + (v / 1e6).toFixed(1) + 'M';
    if (Math.abs(v) >= 1e3) return symbol + (v / 1e3).toFixed(0) + 'K';
    return symbol + Math.round(v);
  }
  return symbol + v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** 精确金额 (不缩写) */
export function fmtPrecise(amount: number, currency: CurrencyValue = Currency.VND): string {
  return CurrencySymbol[currency] + (amount || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** 日期 YYYY-MM-DD */
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '-';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return '-';
  return dt.toISOString().slice(0, 10);
}

/** 日期时间 YYYY-MM-DD HH:mm */
export function fmtDateTime(d: string | Date | null | undefined): string {
  if (!d) return '-';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return '-';
  return dt.toISOString().slice(0, 16).replace('T', ' ');
}

/** 相对时间 (3 分钟前) */
export function fmtRelative(d: string | Date): string {
  const dt = typeof d === 'string' ? new Date(d) : d;
  const seconds = Math.floor((Date.now() - dt.getTime()) / 1000);
  const lang = i18nStore.currentLang;
  const labels: Record<string, { unit: string; vi: string }> = {
    s: { unit: lang === 'vi' ? 'giây trước' : '秒前', vi: '' },
    m: { unit: lang === 'vi' ? 'phút trước' : '分钟前', vi: '' },
    h: { unit: lang === 'vi' ? 'giờ trước' : '小时前', vi: '' },
    d: { unit: lang === 'vi' ? 'ngày trước' : '天前', vi: '' },
  };
  if (seconds < 60) return seconds + labels.s.unit;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return mins + labels.m.unit;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours + labels.h.unit;
  const days = Math.floor(hours / 24);
  return days + labels.d.unit;
}

/** 百分比 */
export function fmtPercent(value: number, decimals = 1): string {
  return (value * 100).toFixed(decimals) + '%';
}

/** 数量 (千分位) */
export function fmtNumber(n: number): string {
  return (n || 0).toLocaleString('en-US');
}
